import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import cx from 'classnames';

import { BedrockAgentType, TBedrockAgentRefineMode } from '@cloud-wave/neon-common-lib';

import { useAIContext } from 'lib/common/contexts/AIContext/Context';

import Icon from 'lib/common/components/Icon';
import { IconSizeConstraints } from 'lib/common/components/Icon/Icon';
import Text from 'lib/common/components/Text';

import { IQActionItems } from '../../../IQActionsMenu/constants/IQActionItems';
import IQMenuContainer from '../../../IQMenuContainer/IQMenuContainer';
import IQModalHeader from '../../../IQModalHeader';
import { IQDisplayMode } from '../../../constants/IQDisplayMode';
import { RefineMenuItemProps } from './types/RefineMenuItemProps';

import styles from './refine-menu-item.module.scss';

export default function RefineMenuItem({ item, displayMode, showAcceptForRewrites }: RefineMenuItemProps) {
  const {
    state: { iqInput, IQFeatureConfig, selectedAction },
    actions: { think, closeIQModal, closeActionMenu, openIQModal, selectMode },
  } = useAIContext();

  const displayModeLower = displayMode.toLowerCase();
  const { title, description, iconProps, modeID } = item;
  const icon = iconProps[displayMode];
  const withEvaluate =
    IQFeatureConfig[BedrockAgentType.AI_REFINE]?.evaluateEnabled &&
    selectedAction === IQActionItems.evaluateEnabled.actionID;

  const onClick = () => {
    const mode = modeID as TBedrockAgentRefineMode;
    selectMode(mode);

    think({
      body: {
        mode,
        userText: iqInput || '',
        withEvaluation: withEvaluate,
      },
      action: BedrockAgentType.AI_REFINE,
    });

    // evaluate menu needs to use the modal version always even if not in standalone mode however don't open a new modal if its already standalone
    if (!withEvaluate || displayMode === IQDisplayMode.STAND_ALONE) {
      return;
    }

    openIQModal({
      title: 'Evaluate',
      customHeader: <IQModalHeader displayMode={displayMode} />,
      content: (
        <IQMenuContainer displayMode={IQDisplayMode.STAND_ALONE} showAcceptForRewrites={showAcceptForRewrites} />
      ),
      onClose: () => {
        closeActionMenu();
        closeIQModal();
      },
    });
  };

  return (
    <button
      className={cx(styles['menu-item'], styles[`menu-item--${displayModeLower}`])}
      onClick={onClick}
      aria-label={title}
    >
      <div className={cx(styles['menu-item__icon'], styles[`menu-item--${displayModeLower}__icon`])}>
        <Icon icon={icon.iconType} size={icon.size} color={icon.color} onlyConstrain={IconSizeConstraints.HEIGHT} />
      </div>
      <div className={styles['menu-item__content']}>
        <div className={styles['menu-item__text']}>
          <Text className={styles[`menu-item__text__title`]}>{title}</Text>
          <Text
            className={cx(
              styles['menu-item__text__description'],
              styles[`menu-item--${displayModeLower}__text__description`]
            )}
          >
            {description}
          </Text>
        </div>
        <div
          className={cx(
            styles['menu-item__content__arrow-container'],
            styles[`menu-item--${displayModeLower}__content__arrow-container`]
          )}
        >
          <Icon icon={faChevronRight} size={15} color={icon.color} />
        </div>
      </div>
    </button>
  );
}
